import { useState } from "react";
import { Heart, HeartOff } from "lucide-react";

import {
  useAddFavourite,
  useFavourites,
  useRemoveFavourite,
} from "@/features/favourites/model/useFavourites";

interface Props {
  workerId: string;
  workerName?: string;
}

export function FavouriteToggleButton({ workerId, workerName }: Props) {
  const firstName = workerName ? workerName.split(" ")[0] : null;
  const { data: favourites } = useFavourites();
  const add = useAddFavourite();
  const remove = useRemoveFavourite();
  const [showNoteField, setShowNoteField] = useState(false);
  const [note, setNote] = useState("");

  const isFav = favourites?.some((f) => f.worker_id === workerId) ?? false;

  if (isFav) {
    return (
      <button
        type="button"
        onClick={() => remove.mutate(workerId)}
        disabled={remove.isPending}
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-copy hover:bg-surface-sunken disabled:opacity-50"
      >
        <HeartOff className="h-3.5 w-3.5" />
        {remove.isPending ? "Removing…" : "Remove from favourites"}
      </button>
    );
  }

  if (!showNoteField) {
    return (
      <button
        type="button"
        onClick={() => setShowNoteField(true)}
        className="inline-flex items-center gap-1.5 rounded-md border border-primary bg-surface px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary-light"
      >
        <Heart className="h-3.5 w-3.5" />
        {firstName ? `Add ${firstName} to favourites` : "Add to favourites"}
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface p-2">
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={500}
        placeholder="Optional note (e.g. great with cocktails)"
        className="min-w-[200px] flex-1 rounded-md border border-border bg-surface px-2 py-1 text-xs"
      />
      <button
        type="button"
        onClick={() =>
          add.mutate(
            { worker_id: workerId, notes: note.trim() || undefined },
            {
              onSuccess: () => {
                setShowNoteField(false);
                setNote("");
              },
            },
          )
        }
        disabled={add.isPending}
        className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
      >
        <Heart className="h-3 w-3" />
        {add.isPending ? "Saving…" : "Save"}
      </button>
      <button
        type="button"
        onClick={() => {
          setShowNoteField(false);
          setNote("");
        }}
        className="text-xs text-copy-muted hover:underline"
      >
        Cancel
      </button>
    </div>
  );
}
